import { useMemo } from 'react';
import { useGlobalWalletSignerClient } from '@abstract-foundation/agw-react';
import { useAccount, useWalletClient } from 'wagmi';
import { BrowserProvider, JsonRpcSigner } from 'ethers';
import type { Eip712Signer } from '../services/wallet/eip712.ts';

const ABSTRACT_CONNECTOR_ID = 'xyz.abs.privy';
const EIP712_DOMAIN_TYPES = [
  { name: 'name', type: 'string' },
  { name: 'version', type: 'string' },
  { name: 'chainId', type: 'uint256' },
  { name: 'verifyingContract', type: 'address' },
];

function getPrimaryType(types: Record<string, Array<{ name: string; type: string }>>) {
  const primaryType = Object.keys(types).find((type) => type !== 'EIP712Domain');
  if (!primaryType) throw new Error('Missing EIP-712 primary type');
  return primaryType;
}

export function useEthersSigner({ chainId }: { chainId?: number } = {}): Eip712Signer | undefined {
  const { connector } = useAccount();
  const { data: walletClient } = useWalletClient({ chainId });
  const { data: abstractSignerClient } = useGlobalWalletSignerClient({ chainId });
  const isAbstractWallet = connector?.id === ABSTRACT_CONNECTOR_ID || connector?.name === 'Abstract';

  return useMemo(() => {
    if (isAbstractWallet) {
      if (!abstractSignerClient?.account) return undefined;
      return {
        getAddress: async () => abstractSignerClient.account.address,
        signTypedData: async (domain, types, value) => {
          const primaryType = getPrimaryType(types);
          return abstractSignerClient.request({
            method: 'eth_signTypedData_v4',
            params: [
              abstractSignerClient.account.address,
              JSON.stringify({
                domain,
                primaryType,
                types: { EIP712Domain: EIP712_DOMAIN_TYPES, ...types },
                message: value,
              }),
            ],
          });
        },
      };
    }

    if (!walletClient) return undefined;
    const { account, chain, transport } = walletClient;
    const network = {
      chainId: chain.id,
      name: chain.name,
      ensAddress: chain.contracts?.ensRegistry?.address,
    };
    const provider = new BrowserProvider(transport, network);
    return new JsonRpcSigner(provider, account.address);
  }, [abstractSignerClient, isAbstractWallet, walletClient]);
}
